import { useEffect, useMemo, useState } from 'react';
import {
	ToothRestorationType,
	ToothSurface,
} from '../../../../../graphql/graphql';
import {
	PRESELECTED_RESTORATION_SURFACES,
	RESTORATION_ITEMS_SURFACES,
} from '../../../Components/DENTModalOverviewRestoration/restorationItemsSurfaces';
import {
	BUTTON_OPTIONS as RESTORATION_BUTTON_OPTIONS,
	RestorationOption,
} from '../../../Helpers/restoration/restorationButtonOptions';
import { useDENTContext } from '../../../Services/DENTContext';
import useTooth from '../useTooth';
import { SurfaceOperation } from '../../../Enums/SurfaceOperation.enum';

export const useToothRestorationSurfaces = (
	surfaceSection?: ToothRestorationType,
) => {
	const restorationButtonOptions = Object.values(RESTORATION_BUTTON_OPTIONS);
	const buttonOption =
		restorationButtonOptions.find(option => option.value === surfaceSection) ||
		restorationButtonOptions[0];

	const [showedSurfaces, setShowedSurfaces] = useState<ToothSurface[]>(
		RESTORATION_ITEMS_SURFACES[buttonOption.value],
	);
	const [selectedSection, setSelectedSection] =
		useState<RestorationOption>(buttonOption);

	const { activeTooth, originalActiveTooth } = useDENTContext();
	const { restoration } = useTooth();

	const restorationHandlers = {
		[ToothRestorationType.Filling]: restoration,
		[ToothRestorationType.Veneer]: restoration,
		[ToothRestorationType.Crown]: { surfaces: [], setSurfaces: () => {} },
		[ToothRestorationType.Inlay]: restoration,
		[ToothRestorationType.Onlay]: restoration,
		[ToothRestorationType.PartialCrown]: restoration,
	};

	useEffect(() => {
		if (surfaceSection) {
			const newButtonOption = restorationButtonOptions.find(
				option => option.value === surfaceSection,
			);
			if (newButtonOption) {
				setShowedSurfaces(RESTORATION_ITEMS_SURFACES[newButtonOption.value]);
				setSelectedSection(newButtonOption);

				const originalType = originalActiveTooth?.restoration?.type;
				const handler = restorationHandlers[surfaceSection];
				const preselectedSurfaces =
					PRESELECTED_RESTORATION_SURFACES[surfaceSection];

				if (surfaceSection === originalType) {
					const originalSurfaces =
						originalActiveTooth?.restoration?.surfaces ?? [];
					handler?.setSurfaces?.(originalSurfaces, SurfaceOperation.REPLACE);
				} else {
					handler?.setSurfaces?.(preselectedSurfaces, SurfaceOperation.REPLACE);
				}
			}
		}
	}, [surfaceSection]);

	const selectedSurfaces = useMemo(() => {
		return restorationHandlers[selectedSection.value]?.surfaces ?? [];
	}, [selectedSection, activeTooth]);

	const handleSelectSurface = (
		surfaces: ToothSurface[],
		operation: SurfaceOperation,
	) => {
		const value = selectedSection.value;
		const handler = restorationHandlers[value];

		if (value === ToothRestorationType.Veneer) {
			handler?.setSurfaces?.(
				surfaces,
				operation === SurfaceOperation.REMOVE
					? SurfaceOperation.REMOVE
					: SurfaceOperation.REPLACE,
			);
		} else {
			handler?.setSurfaces?.(surfaces, operation);
		}
	};

	const handleSelectSection = (section: RestorationOption) => {
		setSelectedSection(section);
		setShowedSurfaces(RESTORATION_ITEMS_SURFACES[section.value]);

		const originalType = originalActiveTooth?.restoration?.type;
		const handler = restorationHandlers[section.value];
		const preselectedSurfaces = PRESELECTED_RESTORATION_SURFACES[section.value];

		if (section.value === originalType) {
			const originalSurfaces = originalActiveTooth?.restoration?.surfaces ?? [];
			handler?.setSurfaces?.(originalSurfaces, SurfaceOperation.REPLACE);
		} else {
			handler?.setSurfaces?.(preselectedSurfaces, SurfaceOperation.REPLACE);
		}
	};

	return {
		showedSurfaces,
		setShowedSurfaces,
		selectedSection,
		setSelectedSection,
		selectedSurfaces,
		handleSelectSurface,
		handleSelectSection,
	} as const;
};
